/**
 * P-36b (D-193, D-196) — the pure logic of the "דיון חדש" form, kept apart from
 * the component so it is unit-tested (the project's frontend tests have no DOM):
 * who may be picked, the counter, the minimum, the payload, the way on after a
 * save. The server applies the same rules (discussion.service, internalWork.service).
 */
import {
  countDiscussionParticipants, discussionChatPath, discussionHasEnoughParticipants,
  splitDiscussionParticipants, minParticipantsFor, MIN_DISCUSSION_PARTICIPANTS, type DiscussionKind,
} from './discussionParticipants';

export interface PickerPerson {
  id: string;
  role: string;
  name: string;
  email?: string;
  organization_id?: string | null;
  /** `employee_profiles.internal_only` (GET /users). */
  internal_only?: boolean;
}

const isStaffRole = (role: string): boolean => role === 'admin' || role === 'agent';
const isCustomerRole = (role: string): boolean => role === 'customer_viewer' || role === 'customer_manager';

/**
 * The OTE side of the list: employees (the opener left out). With a customer
 * chosen, an internal-only employee is not offered (D-196); an internal
 * discussion (no customer) offers everyone.
 */
export function discussionStaffChoices(users: readonly PickerPerson[], openerId: string | null | undefined, hasCustomer: boolean): PickerPerson[] {
  return users.filter((u) => u.id !== openerId && isStaffRole(u.role) && !(hasCustomer && u.internal_only === true));
}

/** The customer's people with a portal login (none without a customer). */
export function discussionCustomerChoices(users: readonly PickerPerson[], hasCustomer: boolean): PickerPerson[] {
  return hasCustomer ? users.filter((u) => isCustomerRole(u.role)) : [];
}

/**
 * P-42 (D-207 Q-191, changes D-196 QX-140) — one row of the customer's people in
 * the discussion form: EVERY contact of the chosen customer (the primary one
 * included), plus logins that are not a contact. `userId` is set only for those
 * with a portal login (they can be picked, as before). A contact with no login
 * is shown greyed: `canCreateAccess` (the caller may grant access to this
 * customer) decides whether the "יצירת גישה" button is offered. A contact whose
 * grant exists but is switched off is greyed without the button (the access
 * screen restores it). Only logins take part in the discussion (ticket_customers).
 */
export interface DiscussionCustomerRow {
  key: string;
  name: string;
  email?: string;
  /** The login to pick; null = no (active) login yet. */
  userId: string | null;
  contactId: string | null;
  canCreateAccess: boolean;
  accessDisabled: boolean;
}
export interface PickerContact {
  id: string;
  name: string;
  email?: string | null;
  active?: boolean;
  can_edit?: boolean;
  portal_access_id?: string | null;
  portal_access_active?: boolean | null;
  portal_access_user_id?: string | null;
}
export function discussionCustomerRows(
  logins: readonly PickerPerson[],
  contacts: readonly PickerContact[],
  hasCustomer: boolean,
): DiscussionCustomerRow[] {
  if (!hasCustomer) return [];
  const contactByUser = new Map<string, PickerContact>();
  for (const c of contacts) if (c.portal_access_user_id && c.portal_access_active) contactByUser.set(c.portal_access_user_id, c);
  const rows: DiscussionCustomerRow[] = [];
  const seenLogins = new Set<string>();
  for (const u of discussionCustomerChoices(logins, hasCustomer)) {
    seenLogins.add(u.id);
    rows.push({ key: `u:${u.id}`, name: u.name, email: u.email, userId: u.id, contactId: contactByUser.get(u.id)?.id ?? null, canCreateAccess: false, accessDisabled: false });
  }
  for (const c of contacts) {
    if (c.active === false) continue;
    if (c.portal_access_user_id && c.portal_access_active) {
      // A live login that the users list did not return: still pickable.
      if (!seenLogins.has(c.portal_access_user_id)) {
        seenLogins.add(c.portal_access_user_id);
        rows.push({ key: `c:${c.id}`, name: c.name, email: c.email ?? undefined, userId: c.portal_access_user_id, contactId: c.id, canCreateAccess: false, accessDisabled: false });
      }
      continue;
    }
    const hasGrant = !!c.portal_access_id;
    rows.push({
      key: `c:${c.id}`, name: c.name, email: c.email ?? undefined, userId: null, contactId: c.id,
      canCreateAccess: !hasGrant && c.can_edit === true, accessDisabled: hasGrant,
    });
  }
  return rows;
}

/** Picks that are still on the list (a customer's people drop out when another customer is chosen; so does an internal-only pick when a customer is chosen). */
export function effectivePickedIds(picked: readonly string[], allowed: readonly { id: string }[]): string[] {
  const ok = new Set(allowed.map((p) => p.id));
  return picked.filter((id) => ok.has(id));
}

/**
 * C-17 (clarity, D-08) — no kind switch: the kind follows the participants. The opener
 * plus one person is a שיחה; three or more are a דיון. The payload keeps sending the
 * same `is_conversation` as before, only computed instead of chosen.
 */
export function kindFromParticipants(count: number): DiscussionKind {
  return count >= MIN_DISCUSSION_PARTICIPANTS ? 'discussion' : 'conversation';
}

export function discussionFormState(openerId: string | null | undefined, picked: readonly string[], kind: DiscussionKind = 'discussion'): { count: number; enough: boolean; min: number } {
  return {
    count: countDiscussionParticipants(openerId, picked),
    enough: discussionHasEnoughParticipants(openerId, picked, kind),
    min: minParticipantsFor(kind),
  };
}

export function buildDiscussionPayload(input: {
  openerId: string;
  title: string;
  message: string;
  organizationId: string;
  picked: readonly string[];
  people: readonly PickerPerson[];
  /** P-45b: 'conversation' sends is_conversation (server: a discussion with 2+ people). Default 'discussion'. */
  kind?: DiscussionKind;
  /** P-45b: optional engagement (the reply-time home); sent only when chosen. */
  engagementId?: string;
}) {
  const { additional_agent_ids, extra_customer_ids } = splitDiscussionParticipants(input.picked, input.people, input.openerId);
  return {
    customer_id: input.openerId,
    title: input.title.trim(),
    description: input.message.trim() || undefined,
    task_type: 'discussion' as const,
    organization_id: input.organizationId || undefined,
    additional_agent_ids: additional_agent_ids.length ? additional_agent_ids : undefined,
    extra_customer_ids: extra_customer_ids.length ? extra_customer_ids : undefined,
    ...(input.kind === 'conversation' ? { is_conversation: true as const } : {}),
    ...(input.kind === 'conversation' && input.engagementId ? { engagement_id: input.engagementId } : {}),
  };
}

/** Submit gate: a subject of 3+ characters and enough participants. */
export function discussionSubmitBlock(title: string, openerId: string | null | undefined, picked: readonly string[], kind: DiscussionKind = 'discussion'): 'title' | 'participants' | null {
  if (title.trim().length < 3) return 'title';
  if (!discussionHasEnoughParticipants(openerId, picked, kind)) return 'participants';
  return null;
}

/** After a successful save the caller opens the new discussion's chat. */
export const pathAfterDiscussionSaved = (created: { id: string } | null | undefined): string | null =>
  created?.id ? discussionChatPath(created.id) : null;

/**
 * FIX-B22 (D-216 QX-195): the participants list stays checkboxes. With more than
 * PARTICIPANT_SEARCH_MIN people a small search box above it narrows the rows by
 * name / e-mail. It only hides rows: the picks themselves are kept as they are.
 */
export const PARTICIPANT_SEARCH_MIN = 8;
export const showParticipantSearch = (peopleCount: number): boolean => peopleCount > PARTICIPANT_SEARCH_MIN;
export function matchesParticipantQuery(person: { name: string; email?: string | null }, query: string): boolean {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = `${person.name ?? ''} ${person.email ?? ''}`.toLocaleLowerCase();
  return words.every((w) => hay.includes(w));
}
